import { BarChart3, BookOpen, Brain, Cake, Calendar, Camera, ClipboardList, Clock, CreditCard, Database, ExternalLink, FileSpreadsheet, GraduationCap, Mail, MapPin, Maximize, MessageSquare, Minimize, Moon, Receipt, Search, Settings, ShieldCheck, Sun, User, Users, Wallet, ZoomIn, ZoomOut } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAnioLectivo } from '../../contexts/AnioLectivoContext';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../hooks/useAuth';
import dayjs from 'dayjs';
import NotificationBell from '../common/NotificationBell';
import './Header.css';

const Header = () => {
    const { theme, toggleTheme, increaseTableZoom, decreaseTableZoom, autoHideHeader, toggleAutoHideHeader } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const { aniosLectivos, selectedAnio, changeAnio } = useAnioLectivo();
    const { user } = useAuth();
    const [isBirthday, setIsBirthday] = useState(false);

    useEffect(() => {
        if (!user) return;
        const birthDateStr = user.personal?.fecha_nacimiento || 
                           user.alumno?.fecha_nacimiento || 
                           user.apoderado_info?.fecha_nacimiento;
        if (!birthDateStr) return;

        const today = dayjs();
        const bDayBase = dayjs(birthDateStr.split('T')[0]); 
        const bDayThisYear = bDayBase.year(today.year());

        // Check if today is the birthday or the day after
        const isActualBirthday = today.isSame(bDayThisYear, 'day');
        const yesterday = today.subtract(1, 'day');
        const isDayAfterBirthday = yesterday.isSame(bDayBase.year(yesterday.year()), 'day');

        if (isActualBirthday || isDayAfterBirthday) {
            setIsBirthday(true);
        } else {
            setIsBirthday(false);
        }
    }, [user, location.pathname]);

    // Auto-Hide hover state
    const [isHeaderRevealed, setIsHeaderRevealed] = useState(false);

    useEffect(() => {
        if (!autoHideHeader) {
            setIsHeaderRevealed(false);
            return;
        }

        const handleMouseMove = (e) => {
            if (e.clientY < 30) {
                // Mouse is near the top edge
                setIsHeaderRevealed(true);
            } else if (e.clientY > 80 && !searchRef.current?.contains(e.target)) {
                // Mouse left the header area and is not interacting with the search dropdown
                setIsHeaderRevealed(false);
            }
        };

        document.addEventListener('mousemove', handleMouseMove);
        return () => document.removeEventListener('mousemove', handleMouseMove);
    }, [autoHideHeader, isHeaderRevealed]);

    const handleYearChange = (e) => {
        const yearId = parseInt(e.target.value);
        const year = aniosLectivos.find(anio => anio.id === yearId);
        if (year) {
            changeAnio(year);
        }
    };

    // Omni-Search State
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const searchRef = useRef(null);

    // Static Pages Index (All Sidebar Routes)
    const PAGES = [
        // Navegación & Perfil
        { title: 'Dashboard / Inicio', path: '/inicio', category: 'Navegación', icon: <Calendar size={14} /> },
        { title: 'Mi Perfil / Datos Personales', path: '/perfil', category: 'Navegación', icon: <User size={14} /> },
        { title: 'Bandeja de Entrada / Mensajes', path: '/email-inbox', category: 'Navegación', icon: <Mail size={14} /> },

        // Usuarios
        { title: 'Estudiantes / Alumnos', path: '/usuarios/estudiantes', category: 'Usuarios', icon: <User size={14} /> },
        { title: 'Personal / Empleados / Profesores', path: '/usuarios/personal', category: 'Usuarios', icon: <Users size={14} /> },
        { title: 'Apoderados / Padres de Familia', path: '/usuarios/apoderados', category: 'Usuarios', icon: <Users size={14} /> },
        { title: 'Lista General Usuarios', path: '/usuarios/lista', category: 'Usuarios', icon: <ClipboardList size={14} /> },
        { title: 'Registro de Nuevo Usuario', path: '/usuarios/nuevo-refactored', category: 'Usuarios', icon: <User size={14} /> },

        // Finanzas & Caja
        { title: 'Gestión de Caja General', path: '/caja/gestion', category: 'Finanzas', icon: <CreditCard size={14} /> },
        { title: 'Gastos / Egresos / Salidas', path: '/caja/gastos', category: 'Finanzas', icon: <Receipt size={14} /> },
        { title: 'Caja Chica', path: '/caja/chica', category: 'Finanzas', icon: <Wallet size={14} /> },
        { title: 'Caja Verano', path: '/caja/verano', category: 'Finanzas', icon: <Wallet size={14} /> },
        { title: 'Caja Academia', path: '/caja/academia', category: 'Finanzas', icon: <Wallet size={14} /> },
        { title: 'Caja Oficina', path: '/caja/oficina', category: 'Finanzas', icon: <Wallet size={14} /> },
        { title: 'Cajas Chicas Admin', path: '/caja/admin', category: 'Finanzas', icon: <ShieldCheck size={14} /> },
        { title: 'Cronogramas de Pago', path: '/caja/cronogramas', category: 'Finanzas', icon: <Calendar size={14} /> },
        { title: 'Recaudación Libre', path: '/caja/recaudacion-libre', category: 'Finanzas', icon: <Wallet size={14} /> },
        { title: 'Reporte de Recaudación', path: '/caja/recaudacion', category: 'Finanzas', icon: <BarChart3 size={14} /> },
        { title: 'Administración Global Caja', path: '/caja/administracion', category: 'Finanzas', icon: <Database size={14} /> },
        { title: 'Configuración de Pagos / Conceptos', path: '/configuracion-pagos', category: 'Finanzas', icon: <Settings size={14} /> },
        { title: 'Comprobantes / Facturas / Boletas', path: '/comprobantes', category: 'Finanzas', icon: <ExternalLink size={14} /> },
        { title: 'Configuración Cuentas Bancarias', path: '/caja/cuentas', category: 'Finanzas', icon: <Database size={14} /> },

        // Académico
        { title: 'Matrícula / Registro Académico', path: '/matriculas', category: 'Académico', icon: <GraduationCap size={14} /> },
        { title: 'Áreas Académicas', path: '/academico/areas', category: 'Académico', icon: <BookOpen size={14} /> },
        { title: 'Asignaturas / Cursos / Materias', path: '/academico/asignaturas', category: 'Académico', icon: <BookOpen size={14} /> },
        { title: 'Malla Curricular', path: '/academico/malla-curricular', category: 'Académico', icon: <Database size={14} /> },
        { title: 'Gestión de Cursos', path: '/academico/cursos', category: 'Académico', icon: <BookOpen size={14} /> },
        { title: 'Gestión de Alumnos', path: '/academico/alumnos', category: 'Académico', icon: <User size={14} /> },
        { title: 'Sábana de Notas', path: '/academico/sabana-notas', category: 'Académico', icon: <ClipboardList size={14} /> },
        { title: 'Libreta de Notas', path: '/academico/libreta-notas', category: 'Académico', icon: <ClipboardList size={14} /> },
        { title: 'Movimiento de Alumnos', path: '/academico/movimiento-alumnos', category: 'Académico', icon: <User size={14} /> },
        { title: 'Registro de Actitud', path: '/academico/actitud', category: 'Académico', icon: <BookOpen size={14} /> },
        { title: 'Sábana MINEDU (Director)', path: '/academico/sabana-minedu', category: 'Académico', icon: <ClipboardList size={14} /> },
        { title: 'Generador de Barcodes / Códigos', path: '/academico/barcodes', category: 'Académico', icon: <Camera size={14} /> },
        { title: 'Estructura Académica / Niveles', path: '/academico/estructura', category: 'Académico', icon: <Settings size={14} /> },
        { title: 'Google Sheets en Vivo', path: '/academico/google-sheets', category: 'Académico', icon: <FileSpreadsheet size={14} /> },

        // Psicología
        { title: 'Mis Citas / Psicoloía', path: '/psicologia/citas', category: 'Psicología', icon: <Brain size={14} /> },
        { title: 'Agenda / Horarios Psicología', path: '/psicologia/agenda', category: 'Psicología', icon: <Clock size={14} /> },
        { title: 'Evaluaciones / Plantillas Psico', path: '/psicologia/evaluaciones', category: 'Psicología', icon: <ClipboardList size={14} /> },
        { title: 'Disponibilidad Admisiones', path: '/psicologia/disponibilidad-admisiones', category: 'Psicología', icon: <Brain size={14} /> },

        // Portales Estudiante & Apoderado
        { title: 'Portal: Mis Cursos (Alumno)', path: '/alumno/mis-cursos', category: 'Portal', icon: <BookOpen size={14} /> },
        { title: 'Portal: Mis Calificaciones (Alumno)', path: '/alumno/mis-calificaciones', category: 'Portal', icon: <ClipboardList size={14} /> },
        { title: 'Portal: Mi Asistencia (Alumno)', path: '/alumno/mi-asistencia', category: 'Portal', icon: <Calendar size={14} /> },
        { title: 'Portal: Mi Horario (Alumno)', path: '/alumno/mi-horario', category: 'Portal', icon: <Clock size={14} /> },
        { title: 'Portal: Notas de Hijos (Apoderado)', path: '/apoderado/notas', category: 'Portal', icon: <Users size={14} /> },
        { title: 'Portal: Asistencia de Hijos (Apod)', path: '/apoderado/asistencia', category: 'Portal', icon: <Calendar size={14} /> },
        { title: 'Portal: Pagos Pendientes (Apod)', path: '/apoderado/pagos', category: 'Portal', icon: <CreditCard size={14} /> },

        // Configuración & Seguridad
        { title: 'Roles y Permisos', path: '/seguridad/roles', category: 'Seguridad', icon: <ShieldCheck size={14} /> },
        { title: 'Gestión de Sedes', path: '/sedes', category: 'Configuración', icon: <MapPin size={14} /> },
        { title: 'Niveles Académicos', path: '/niveles', category: 'Configuración', icon: <Settings size={14} /> },
        { title: 'Grados', path: '/grados', category: 'Configuración', icon: <Settings size={14} /> },
        { title: 'Aulas', path: '/aulas', category: 'Configuración', icon: <Settings size={14} /> },
        { title: 'Años Lectivos', path: '/anios-lectivos', category: 'Configuración', icon: <Settings size={14} /> },
        { title: 'Configuración Horarios Laborales', path: '/configuracion/horarios', category: 'Configuración', icon: <Clock size={14} /> },
        { title: 'Prueba WhatsApp / Bots', path: '/whatsapp-test', category: 'Configuración', icon: <MessageSquare size={14} /> },
    ];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowResults(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle Keyboard Shortcuts (Ctrl + K)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                searchRef.current?.querySelector('input')?.focus();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        const fetchResults = async () => {
            if (searchTerm.trim().length < 2) {
                setResults([]);
                setShowResults(false);
                return;
            }

            setLoading(true);
            setShowResults(true);
            setSelectedIndex(0);

            // Normalize strings for fuzzy search (Accent insensitive, case insensitive)
            const normalize = (str) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
            const normalizedSearch = normalize(searchTerm);

            // 1. Filter local pages
            const filteredPages = PAGES.filter(p =>
                normalize(p.title).includes(normalizedSearch) ||
                normalize(p.category).includes(normalizedSearch)
            ).map(p => ({ ...p, type: 'page' }));

            // 2. Search Alumnos (Async)
            let students = [];
            try {
                const response = await cajaService.buscarAlumnos(null, searchTerm);
                students = response.slice(0, 5).map(s => ({
                    title: s.apellidos_nombres,
                    subtitle: `DNI: ${s.dni}`,
                    path: `/academico/alumnos`, // Enviar a lista con filtro o detalle si existiera
                    category: 'Alumnos',
                    icon: <User size={14} />,
                    type: 'entity',
                    raw: s
                }));
            } catch (error) {
                console.error("Search error:", error);
            }

            setResults([...filteredPages, ...students]);
            setLoading(false);
        };

        const timeoutId = setTimeout(fetchResults, 300);
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    const handleKeyDown = (e) => {
        if (!showResults || results.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => (prev + 1) % results.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => (prev - 1 + results.length) % results.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            handleSelect(results[selectedIndex]);
        } else if (e.key === 'Escape') {
            setShowResults(false);
        }
    };

    const handleSelect = (item) => {
        navigate(item.path);
        setSearchTerm('');
        setShowResults(false);
    };

    const getPageTitle = (path) => {
        if (path === '/' || path === '/inicio') return 'Inicio';

        // Usuarios
        if (path === '/usuarios/estudiantes') return 'Lista de Estudiantes';
        if (path === '/usuarios/personal') return 'Lista de Personal';
        if (path === '/usuarios/apoderados') return 'Lista de Apoderados';
        if (path === '/usuarios/lista') return 'Lista General de Usuarios';
        if (path.includes('/usuarios/nuevo-refactored')) return 'Registro de Usuario';

        // Caja / Pagos
        if (path === '/caja/cronogramas') return 'Cronogramas de Pago';
        if (path === '/caja/gestion') return 'Gestión de Caja';
        if (path === '/caja/gastos') return 'Gastos y Egresos';
        if (path === '/caja/recaudacion-libre') return 'Recaudación Libre';
        if (path === '/caja/recaudacion') return 'Reporte de Recaudación';
        if (path === '/caja/cuentas') return 'Configuración de Cuentas';
        if (path === '/caja/administracion') return 'Administración Global de Caja';
        if (path === '/configuracion-pagos') return 'Configuración de Pagos';
        if (path === '/comprobantes') return 'Lista de Comprobantes';

        // Contabilidad
        if (path === '/contabilidad/sunat') return 'SIRE · Facturación Electrónica SUNAT';
        if (path === '/contabilidad/ingresos') return 'Reporte de Ingresos';
        if (path === '/contabilidad/egresos') return 'Reporte de Egresos';
        if (path === '/contabilidad/balance') return 'Balance General';

        // Módulo Académico
        if (path === '/academico/areas') return 'Áreas Académicas';
        if (path === '/academico/asignaturas') return 'Asignaturas';
        if (path === '/academico/malla-curricular') return 'Malla Curricular';
        if (path === '/academico/cursos') return 'Gestión de Cursos';
        if (path === '/academico/alumnos') return 'Gestión de Alumnos';
        if (path === '/academico/sabana-notas') return 'Sábana de Notas';
        if (path === '/academico/libreta-notas') return 'Libreta de Notas';
        if (path === '/academico/barcodes') return 'Generador de Barcodes';
        if (path === '/academico/estructura') return 'Estructura Académica';
        if (path === '/academico/unidades') return 'Gestión de Unidades';
        if (path === '/academico/tipos-actividad') return 'Tipo Actividades';
        if (path === '/academico/movimiento-alumnos') return 'Movimiento de Alumnos';
        if (path === '/academico/actitud') return 'Registro de Actitud';
        if (path === '/academico/sabana-minedu') return 'Sábana MINEDU (Director)';
        if (path.includes('/academico/sesiones')) return 'Materiales de Clase';
        if (path === '/academico/google-sheets') return 'Google Sheets en Vivo';

        // Matriculas
        if (path === '/matriculas' || path === '/matriculas/nueva') return 'Gestión de Matrículas';

        // Psicología
        if (path.startsWith('/psicologia/plantillas')) return 'Plantillas de Psicología';
        if (path.startsWith('/psicologia/evaluaciones')) return 'Evaluaciones Psicológicas';
        if (path === '/psicologia/citas') return 'Gestión de Citas';
        if (path === '/psicologia/horario' || path === '/psicologia/agenda') return 'Agenda Psicológica';
        if (path === '/psicologia/disponibilidad-admisiones') return 'Disponibilidad Admisiones';

        // Otros
        if (path === '/seguridad/roles') return 'Roles y Permisos';
        if (path === '/configuracion/horarios') return 'Configuración de Horarios';
        if (path.startsWith('/sedes')) return 'Gestión de Sedes';
        if (path === '/niveles') return 'Gestión de Niveles';
        if (path === '/grados') return 'Gestión de Grados';
        if (path === '/aulas') return 'Gestión de Aulas';
        if (path === '/anios-lectivos') return 'Años Lectivos';
        if (path === '/academico/calendario') return 'Calendario 2026';

        // Admin Custom Pages
        if (path === '/admin/asistencia-aula') return 'Asistencia por Aula';
        if (path === '/admin/justificaciones') return 'Justificaciones de Asistencia';
        if (path === '/admin/asistencia-control') return 'Control de Asistencia';
        if (path === '/admin/auditoria-carnets') return 'Auditoría de Carnets';
        if (path === '/admin/auditoria-carnets-personal') return 'Auditoría Carnets Personal';
        if (path === '/admin/lotes-carnets') return 'Lotes de Producción';
        if (path.startsWith('/admin/lotes-carnets/')) return 'Detalle de Lote';
        if (path === '/admin/quitar-fondo') return 'Removedor de Fondos';

        // Portales Estudiante/Apoderado
        if (path === '/alumno/mis-cursos') return 'Mis Cursos';
        if (path === '/alumno/mi-aula' || path === '/academico/mi-aula') {
            const courseName = localStorage.getItem('currentCourseName');
            return courseName ? `Mis Cursos > ${courseName}` : 'Mi Aula';
        }
        if (path === '/alumno/mis-calificaciones') return 'Mis Calificaciones';
        if (path === '/alumno/mi-horario') return 'Mi Horario';
        if (path === '/alumno/mi-asistencia') return 'Mi Asistencia';
        if (path.startsWith('/alumno')) return 'Portal del Estudiante';
        if (path.startsWith('/apoderado')) return 'Portal del Apoderado';

        // Portal Docente
        if (path === '/docente/importar-masivo') return 'Importar Notas';
        if (path === '/docente/mis-cursos') return 'Mis Cursos';
        if (path === '/academico/aula-docente') return 'Aula Virtual Docente';
        if (path.startsWith('/docente/unidad/')) return 'Detalle de Unidad';
        if (path.startsWith('/docente')) return 'Portal Docente';

        // Personal
        if (path.startsWith('/personal')) return 'ASISTENCIA DEL PERSONAL';

        // Fallback for sub-routes
        if (path.startsWith('/usuarios')) return 'Gestión de Usuarios';
        if (path.startsWith('/matriculas')) return 'Matrícula';
        // Caja
        if (path === '/caja/chica') return 'Caja Chica';
        if (path === '/caja/verano') return 'Caja Verano';
        if (path === '/caja/academia') return 'Caja Academia';
        if (path === '/caja/oficina') return 'Caja Oficina';
        if (path === '/caja/uniformes') return 'Caja Uniformes';
        if (path.startsWith('/caja')) return 'Módulo de Caja';
        if (path.startsWith('/psicologia')) return 'Psicología';

        return 'Dashboard';
    };

    return (
        <div
            className={`header ${autoHideHeader ? 'auto-hide-enabled' : ''} ${isHeaderRevealed ? 'header-revealed' : ''}`}
            onMouseEnter={() => { if (autoHideHeader) setIsHeaderRevealed(true); }}
            onMouseLeave={(e) => {
                if (autoHideHeader && e.clientY > 44 && !searchRef.current?.contains(e.target)) {
                    setIsHeaderRevealed(false);
                }
            }}
        >
            <div className="header-content">
                <nav className="navbar navbar-expand">
                    <div className="collapse navbar-collapse justify-content-between">
                        <div className="header-left">
                            <div className="dashboard_bar d-none d-sm-block">
                                {getPageTitle(location.pathname)}
                                {isBirthday && (
                                    <span className="ms-3 badge bg-warning text-dark d-none d-md-inline-flex align-items-center gap-1" style={{ fontSize: '0.75rem' }}>
                                        <Cake size={14} /> ¡Feliz Cumpleaños!
                                    </span>
                                )}
                            </div>
                        </div>
                        <ul className="navbar-nav header-right">
                            <li className="nav-item">
                                <div className="search-area-wrapper" ref={searchRef}>
                                    <div className="search-area" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.35rem',
                                        background: 'transparent',
                                        padding: window.innerWidth < 576 ? '0.15rem 0.2rem' : '0.15rem 0.35rem',
                                        transition: 'all 0.3s ease'
                                    }}>
                                        <Search size={12} style={{ opacity: 0.5, flexShrink: 0 }} />
                                        <input
                                            type="text"
                                            className="form-control search-input"
                                            placeholder="Buscar aquí..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            onFocus={() => searchTerm.trim().length >= 2 && setShowResults(true)}
                                            style={{
                                                border: '1px solid rgba(0, 0, 0, 0.1)',
                                                background: 'rgba(0, 0, 0, 0.03)',
                                                borderRadius: '50px',
                                                padding: '0.05rem 0.55rem',
                                                height: '22px',
                                                fontSize: '0.7rem',
                                                boxShadow: 'none',
                                                outline: 'none',
                                                minWidth: window.innerWidth < 576 ? '80px' : '125px',
                                                transition: 'all 0.3s ease'
                                            }}
                                        />
                                    </div>

                                    {showResults && (
                                        <div className="omni-results-dropdown">
                                            {loading && results.length === 0 ? (
                                                <div className="search-status p-3 text-center">
                                                    <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                                                    Buscando...
                                                </div>
                                            ) : results.length > 0 ? (
                                                <div className="results-list">
                                                    {Array.from(new Set(results.map(r => r.category))).map(cat => (
                                                        <div key={cat} className="category-group">
                                                            <div className="category-header">{cat}</div>
                                                            {results.filter(r => r.category === cat).map(item => {
                                                                const globalIndex = results.indexOf(item);
                                                                return (
                                                                    <div
                                                                        key={item.path + item.title}
                                                                        className={`result-item ${selectedIndex === globalIndex ? 'selected' : ''}`}
                                                                        onClick={() => handleSelect(item)}
                                                                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                                                                    >
                                                                        <div className="item-icon">{item.icon}</div>
                                                                        <div className="item-details">
                                                                            <div className="item-title">{item.title}</div>
                                                                            {item.subtitle && <div className="item-subtitle">{item.subtitle}</div>}
                                                                        </div>
                                                                        {selectedIndex === globalIndex && <div className="item-action">Enter ↵</div>}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : searchTerm.length >= 2 && (
                                                <div className="search-status p-3 text-center text-muted">
                                                    No se encontraron resultados para "{searchTerm}"
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </li>
                            <li className="nav-item dropdown notification_dropdown d-flex align-items-center">
                                <button
                                     className="nav-link p-0 me-1"
                                     onClick={(e) => {
                                         e.preventDefault();
                                         decreaseTableZoom();
                                     }}
                                     title="Disminuir letra de tabla"
                                     style={{ border: 'none', background: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                                 >
                                     <ZoomOut size={14} />
                                 </button>
                                 <button
                                     className="nav-link p-0"
                                     onClick={(e) => {
                                         e.preventDefault();
                                         increaseTableZoom();
                                     }}
                                     title="Aumentar letra de tabla"
                                     style={{ border: 'none', background: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                                 >
                                     <ZoomIn size={14} />
                                 </button>
                            </li>
                            <li className="nav-item dropdown notification_dropdown">
                                <button
                                     className={`nav-link p-0 ${autoHideHeader ? 'active' : ''}`}
                                     onClick={(e) => {
                                         e.preventDefault();
                                         toggleAutoHideHeader();
                                     }}
                                     title={autoHideHeader ? "Fijar Cabecera" : "Ocultar Cabecera (Auto-Hide)"}
                                     style={{ border: 'none', background: 'none', color: autoHideHeader ? 'var(--primary)' : 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                                 >
                                     {autoHideHeader ? <Minimize size={14} /> : <Maximize size={14} />}
                                 </button>
                            </li>
                            <li className="nav-item dropdown notification_dropdown">
                                <a
                                     className={`nav-link bell dz-theme-mode p-0 ${theme === 'dark' ? 'active' : ''}`}
                                     href="#"
                                     onClick={(e) => {
                                         e.preventDefault();
                                         toggleTheme();
                                     }}
                                     style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                                 >
                                     {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
                                 </a>
                            </li>
                            <li className="nav-item dropdown notification_dropdown">
                                <NotificationBell />
                            </li>
                            <li className="nav-item d-flex align-items-center">
                                {/* Year Selector */}
                                <select
                                    className="year-selector"
                                    style={{
                                        width: 'auto',
                                        minWidth: window.innerWidth < 576 ? '70px' : '86px',
                                        padding: window.innerWidth < 576 ? '0 0.75rem 0 1.1rem' : '0 0.9rem 0 1.25rem',
                                        backgroundPosition: window.innerWidth < 576 ? 'left 0.35rem center, right 0.35rem center' : 'left 0.45rem center, right 0.45rem center',
                                        backgroundSize: window.innerWidth < 576 ? '8.5px, 5px' : '9px, 5.5px',
                                        verticalAlign: 'middle',
                                        display: 'inline-flex',
                                        alignItems: 'center'
                                    }}
                                    value={selectedAnio?.id || ''}
                                    onChange={handleYearChange}
                                >
                                    {aniosLectivos.map(anio => (
                                        <option
                                            key={anio.id}
                                            value={anio.id}
                                            style={{
                                                backgroundColor: theme === 'dark' ? '#1e1e24' : '#ffffff',
                                                color: theme === 'dark' ? '#ffffff' : '#000000',
                                                padding: '6px 8px',
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            {anio.nombre}
                                        </option>
                                    ))}
                                </select>
                            </li>
                        </ul>
                    </div>
                </nav>
            </div>
        </div>
    );
};

export default Header;
